"use client";

import type { operations } from "@tamariba/api-client";
import { usePathname } from "next/navigation";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export type Player =
  operations["getCurrentPlayer"]["responses"][200]["content"]["application/json"];
type Status = "loading" | "anonymous" | "registration" | "ready" | "error";
type PlayerFocus = {
  onExit?: () => void;
  exitDisabled: boolean;
  hideDesktopNavigation: boolean;
};
type Session = {
  player?: Player;
  status: Status;
  focused: boolean;
  focus?: PlayerFocus;
  setFocus: (value: PlayerFocus | undefined) => void;
  refresh: () => Promise<void>;
  acceptPlayer: (player: Player) => void;
};
const SessionContext = createContext<Session | undefined>(undefined);

export function PlayerSession({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const isolated =
    /^\/(admin|store|login|signup|forgot-password|auth)(\/|$)/.test(pathname);
  const [player, setPlayer] = useState<Player>();
  const [status, setStatus] = useState<Status>("loading");
  const [focus, setFocus] = useState<PlayerFocus>();
  const acceptPlayer = useCallback((next: Player) => {
    setPlayer(next);
    setStatus("ready");
  }, []);
  const refresh = useCallback(async () => {
    setStatus("loading");
    setPlayer(undefined);
    try {
      const response = await fetch("/api/players/me", {
        cache: "no-store",
        signal: AbortSignal.timeout(15000),
      });
      if (response.status === 401) setStatus("anonymous");
      else if (response.status === 404) setStatus("registration");
      else if (response.ok) acceptPlayer((await response.json()) as Player);
      else setStatus("error");
    } catch {
      setStatus("error");
    }
  }, [acceptPlayer]);
  useEffect(() => {
    if (!isolated) void refresh();
  }, [isolated, refresh]);
  return (
    <SessionContext.Provider
      value={{
        player,
        status,
        focused: !!focus,
        focus,
        setFocus,
        refresh,
        acceptPlayer,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function usePlayerSession() {
  const session = useContext(SessionContext);
  if (!session) throw new Error("PlayerSession is required");
  return session;
}

export function usePlayerFocus(
  active: boolean,
  onExit?: () => void,
  exitDisabled = false,
  hideDesktopNavigation = false,
) {
  const { setFocus } = usePlayerSession();
  const exitRef = useRef(onExit);
  useEffect(() => {
    exitRef.current = onExit;
  }, [onExit]);
  const exit = useCallback(() => exitRef.current?.(), []);
  const hasExit = !!onExit;
  useEffect(() => {
    if (!active) return;
    setFocus({
      onExit: hasExit ? exit : undefined,
      exitDisabled,
      hideDesktopNavigation,
    });
    return () => setFocus(undefined);
  }, [active, hasExit, exit, exitDisabled, hideDesktopNavigation, setFocus]);
}

export function PlayerAccess({
  children,
  description = "お店のイベントやお知らせを確認して、次に撞くきっかけを見つけよう。",
}: {
  children: ReactNode;
  description?: string;
}) {
  const { status, refresh } = usePlayerSession();
  if (status === "ready") return children;
  if (status === "loading") return <p role="status">読み込み中…</p>;
  if (status === "error")
    return (
      <div className="player-empty" role="alert">
        <h2>接続を確認できませんでした</h2>
        <p>通信環境を確認して、もう一度お試しください。</p>
        <button
          className="secondary-button"
          type="button"
          onClick={() => void refresh()}
        >
          再読み込み
        </button>
      </div>
    );
  return (
    <div className="player-empty">
      <h2>
        {status === "registration"
          ? "プレイヤー登録を完了してください"
          : "ログインして、続きを見よう"}
      </h2>
      <p>{description}</p>
      <a
        className="primary-button"
        href={status === "registration" ? "/account" : "/login"}
      >
        {status === "registration" ? "登録を完了する" : "ログイン"}
      </a>
    </div>
  );
}
